import { useEffect, useState } from 'react';
import Link from '@/components/compat/Link';
import { ExternalLink, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useCms } from '@/components/painel/cms/cms-context';
import { usePainelText } from '@/components/painel/painel-texts-context';
import { painelInputClass, painelTextareaClass } from '@/components/painel/cms/painel-form';
import {
  painelButtonOutlineClass,
  painelButtonPrimaryClass,
  painelButtonResetRingClass,
  painelDescriptionClass,
  painelSurfaceClass,
} from '@/components/painel/painel-styles';
import type { SiteFooter, SiteFooterColumnId, SiteIdentity } from '@/lib/cms-types';
import {
  buildDefaultFooter,
  buildDefaultIdentity,
  normalizeFooter,
  normalizeIdentity,
  DEFAULT_FOOTER_PEEK_SMILIES,
} from '@/lib/site-defaults';
import { XAT_AJUDA_VERSION } from '@/lib/site-version';
import { updateFooter } from '@/services/site-content.service';
import { cn } from '@/lib/utils';
import { GsImage } from '@/components/help-center/latest-power-shared';

const COLUMN_HINT: Record<SiteFooterColumnId, string> = {
  product: 'Coluna Produto',
  support: 'Coluna Suporte',
  community: 'Coluna Comunidade',
  legal: 'Coluna Legal',
};

export function PainelFooterEditorPage() {
  const pageText = usePainelText('identidade');
  const { site, setSite, hydrated } = useCms();
  const [form, setForm] = useState<SiteFooter>(() => normalizeFooter(site.footer));
  const [identity, setIdentity] = useState<SiteIdentity>(() =>
    normalizeIdentity(site.identity)
  );
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!hydrated || dirty) return;
    setForm(normalizeFooter(site.footer));
    setIdentity(normalizeIdentity(site.identity));
  }, [hydrated, site.footer, site.identity, dirty]);

  function patchForm(patch: Partial<SiteFooter>) {
    setForm((prev) => normalizeFooter({ ...prev, ...patch }));
    setDirty(true);
  }

  function patchIdentity(patch: Partial<SiteIdentity>) {
    setIdentity((prev) => normalizeIdentity({ ...prev, ...patch }));
    setDirty(true);
  }

  function patchPeekSmilie(index: number, value: string) {
    setForm((prev) => {
      const next = [...prev.peekSmilies] as SiteFooter['peekSmilies'];
      next[index] = value;
      return { ...prev, peekSmilies: next };
    });
    setDirty(true);
  }

  function patchColumn(columnId: SiteFooterColumnId, patch: { title?: string }) {
    setForm((prev) =>
      normalizeFooter({
        ...prev,
        columns: prev.columns.map((col) =>
          col.id === columnId ? { ...col, ...patch } : col
        ),
      })
    );
    setDirty(true);
  }

  function patchLink(
    columnId: SiteFooterColumnId,
    linkIndex: number,
    patch: { label?: string; href?: string }
  ) {
    setForm((prev) =>
      normalizeFooter({
        ...prev,
        columns: prev.columns.map((col) => {
          if (col.id !== columnId) return col;
          return {
            ...col,
            links: col.links.map((link, i) => (i === linkIndex ? { ...link, ...patch } : link)),
          };
        }),
      })
    );
    setDirty(true);
  }

  function addLink(columnId: SiteFooterColumnId) {
    setForm((prev) =>
      normalizeFooter({
        ...prev,
        columns: prev.columns.map((col) =>
          col.id === columnId
            ? { ...col, links: [...col.links, { label: 'Novo link', href: '#' }] }
            : col
        ),
      })
    );
    setDirty(true);
  }

  function removeLink(columnId: SiteFooterColumnId, linkIndex: number) {
    setForm((prev) =>
      normalizeFooter({
        ...prev,
        columns: prev.columns.map((col) =>
          col.id === columnId
            ? { ...col, links: col.links.filter((_, i) => i !== linkIndex) }
            : col
        ),
      })
    );
    setDirty(true);
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = normalizeFooter(form);
      const identityPayload = normalizeIdentity(identity);
      const next = await updateFooter(payload, identityPayload);
      const footer = normalizeFooter(next.footer);
      const nextIdentity = normalizeIdentity(next.identity);
      setForm(footer);
      setIdentity(nextIdentity);
      setSite({ ...next, footer, identity: nextIdentity }, { persist: false });
      setDirty(false);
      toast.success('Identidade guardada.', {
        description: 'Marca, favicon e rodapé já aparecem no site público.',
      });
    } catch {
      toast.error('Não foi possível guardar a identidade do site.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(buildDefaultFooter());
    setIdentity(buildDefaultIdentity());
    setDirty(true);
    toast.message('Formulário reposto aos valores padrão. Guarde para aplicar.');
  };

  const previewVersion =
    form.changelog.version.trim().length > 0
      ? form.changelog.version.trim()
      : XAT_AJUDA_VERSION;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-inter text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300/55">
            System
          </p>
          <h1 className="mt-1 font-poppins text-2xl font-bold text-white md:text-3xl">
            {pageText.title}
          </h1>
          <p className={cn('mt-2 max-w-2xl text-sm', painelDescriptionClass)}>
            {pageText.description}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          asChild
          className={cn(painelButtonOutlineClass, painelButtonResetRingClass, 'h-10 shrink-0')}
        >
          <Link href="/" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="mr-2 h-4 w-4" />
            Ver no site
          </Link>
        </Button>
      </div>

      <div className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8', !hydrated && 'opacity-70')}>
        <div>
          <h2 className="font-poppins text-lg font-bold text-white">Identidade</h2>
          <p className="mt-1 font-inter text-sm text-slate-400">
            Título da navbar, subtítulo do site e favicon do navegador.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="site-title" className="text-blue-100/90">
              Título do site
            </Label>
            <input
              id="site-title"
              value={identity.title}
              onChange={(e) => patchIdentity({ title: e.target.value })}
              className={painelInputClass}
              placeholder="xat Ajuda"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="site-subtitle" className="text-blue-100/90">
              Subtítulo
            </Label>
            <input
              id="site-subtitle"
              value={identity.subtitle}
              onChange={(e) => patchIdentity({ subtitle: e.target.value })}
              className={painelInputClass}
              placeholder="Central de Suporte"
            />
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="site-favicon" className="text-blue-100/90">
            URL do favicon
          </Label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              id="site-favicon"
              value={identity.faviconUrl}
              onChange={(e) => patchIdentity({ faviconUrl: e.target.value })}
              className={cn(painelInputClass, 'min-w-0 flex-1')}
              placeholder="/favicon.ico ou https://…"
            />
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/5">
              <img
                src={identity.faviconUrl.trim() || '/favicon.ico'}
                alt=""
                className="h-6 w-6 object-contain"
                onError={(e) => {
                  e.currentTarget.style.opacity = '0.35';
                }}
              />
            </span>
          </div>
          <p className="font-inter text-xs text-slate-500">
            Deixe vazio para usar o favicon padrão em <code className="text-slate-400">/favicon.ico</code>.
          </p>
        </div>
      </div>

      <div className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8', !hydrated && 'opacity-70')}>
        <h2 className="font-poppins text-lg font-bold text-white">Marca do rodapé</h2>
        <div className="grid gap-2">
          <Label htmlFor="footer-brand" className="text-blue-100/90">
            Nome
          </Label>
          <input
            id="footer-brand"
            value={form.brandName}
            onChange={(e) => patchForm({ brandName: e.target.value })}
            className={painelInputClass}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="footer-brand-desc" className="text-blue-100/90">
            Descrição
          </Label>
          <textarea
            id="footer-brand-desc"
            value={form.brandDescription}
            onChange={(e) => patchForm({ brandDescription: e.target.value })}
            className={cn(painelTextareaClass, 'min-h-[80px]')}
          />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {form.columns.map((column) => (
          <div
            key={column.id}
            className={cn(painelSurfaceClass, 'space-y-4 p-6 md:p-7', !hydrated && 'opacity-70')}
          >
            <div className="grid gap-2">
              <Label htmlFor={`footer-col-${column.id}`} className="text-blue-100/90">
                {COLUMN_HINT[column.id]} — título
              </Label>
              <input
                id={`footer-col-${column.id}`}
                value={column.title}
                onChange={(e) => patchColumn(column.id, { title: e.target.value })}
                className={painelInputClass}
              />
            </div>
            <div className="space-y-3">
              <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-400">
                Links
              </p>
              {column.links.map((link, index) => (
                <div
                  key={`${column.id}-${index}`}
                  className="grid gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3 sm:grid-cols-[1fr_1fr_auto]"
                >
                  <input
                    aria-label={`Texto do link ${index + 1}`}
                    value={link.label}
                    onChange={(e) => patchLink(column.id, index, { label: e.target.value })}
                    placeholder="Texto"
                    className={painelInputClass}
                  />
                  <input
                    aria-label={`URL do link ${index + 1}`}
                    value={link.href}
                    onChange={(e) => patchLink(column.id, index, { href: e.target.value })}
                    placeholder="/caminho ou https://…"
                    className={painelInputClass}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0 text-slate-400 hover:bg-red-500/15 hover:text-red-300"
                    onClick={() => removeLink(column.id, index)}
                    aria-label="Remover link"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                className={cn(painelButtonOutlineClass, 'h-9')}
                onClick={() => addLink(column.id)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Adicionar link
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8', !hydrated && 'opacity-70')}>
        <div>
          <h2 className="font-poppins text-lg font-bold text-white">Smilies acima da logo</h2>
          <p className="mt-1 font-inter text-sm text-slate-400">
            Cinco códigos de smiley do xat (esquerda → direita), no arco flutuante do rodapé.
            Exemplos: tongue, wink, cool, smile, cd.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-5">
          {form.peekSmilies.map((code, index) => (
            <div key={index} className="grid gap-2">
              <Label htmlFor={`footer-peek-${index}`} className="text-blue-100/90">
                Smiley {index + 1}
              </Label>
              <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2">
                <GsImage
                  code={code || DEFAULT_FOOTER_PEEK_SMILIES[index]}
                  className="flex h-10 w-10 shrink-0 items-center justify-center"
                  imgClassName="max-h-9 max-w-9 object-contain"
                />
                <input
                  id={`footer-peek-${index}`}
                  value={code}
                  onChange={(e) => patchPeekSmilie(index, e.target.value)}
                  placeholder={DEFAULT_FOOTER_PEEK_SMILIES[index]}
                  className={cn(painelInputClass, 'min-w-0 flex-1')}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8', !hydrated && 'opacity-70')}>
        <h2 className="font-poppins text-lg font-bold text-white">Lojas de apps</h2>
        <div className="grid gap-2">
          <Label htmlFor="footer-apps-section-title" className="text-blue-100/90">
            Título da secção
          </Label>
          <input
            id="footer-apps-section-title"
            value={form.appsSectionTitle}
            onChange={(e) => patchForm({ appsSectionTitle: e.target.value })}
            placeholder="Use o xat no celular"
            className={painelInputClass}
          />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="footer-appstore-url" className="text-blue-100/90">
              URL App Store
            </Label>
            <input
              id="footer-appstore-url"
              value={form.appStoreUrl}
              onChange={(e) => patchForm({ appStoreUrl: e.target.value })}
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="footer-appstore-aria" className="text-blue-100/90">
              Título do botão App Store
            </Label>
            <input
              id="footer-appstore-aria"
              value={form.appStoreAriaLabel}
              onChange={(e) => patchForm({ appStoreAriaLabel: e.target.value })}
              placeholder="Baixar na App Store"
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="footer-play-url" className="text-blue-100/90">
              URL Google Play
            </Label>
            <input
              id="footer-play-url"
              value={form.googlePlayUrl}
              onChange={(e) => patchForm({ googlePlayUrl: e.target.value })}
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="footer-play-aria" className="text-blue-100/90">
              Título do botão Google Play
            </Label>
            <input
              id="footer-play-aria"
              value={form.googlePlayAriaLabel}
              onChange={(e) => patchForm({ googlePlayAriaLabel: e.target.value })}
              placeholder="Baixar no Google Play"
              className={painelInputClass}
            />
          </div>
        </div>
        <p className="font-inter text-xs text-slate-500">
          O título dos botões aparece no atributo acessível (aria/title/alt). O visual do badge
          oficial da loja não muda.
        </p>
      </div>

      <div className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8', !hydrated && 'opacity-70')}>
        <h2 className="font-poppins text-lg font-bold text-white">Copyright e aviso</h2>
        <div className="grid gap-2">
          <Label htmlFor="footer-copyright" className="text-blue-100/90">
            Copyright
          </Label>
          <input
            id="footer-copyright"
            value={form.copyright}
            onChange={(e) => patchForm({ copyright: e.target.value })}
            className={painelInputClass}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="footer-disclaimer" className="text-blue-100/90">
            Aviso de marcas (abaixo do copyright)
          </Label>
          <textarea
            id="footer-disclaimer"
            value={form.disclaimer}
            onChange={(e) => patchForm({ disclaimer: e.target.value })}
            rows={4}
            className={cn(painelInputClass, 'h-auto min-h-[96px] resize-y py-2.5 leading-relaxed')}
          />
          <p className="font-inter text-xs text-slate-500">
            Texto discreto no rodapé público — mantenha curto o suficiente para não poluir.
          </p>
        </div>
      </div>

      <div className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8', !hydrated && 'opacity-70')}>
        <div>
          <h2 className="font-poppins text-lg font-bold text-white">Botão de changelog</h2>
          <p className="mt-1 font-inter text-sm text-slate-400">
            Pré-visualização: «{form.changelog.prefix} · v{previewVersion}». Deixe a versão vazia
            para usar a versão do site ({XAT_AJUDA_VERSION}).
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="grid gap-2">
            <Label htmlFor="footer-cl-prefix" className="text-blue-100/90">
              Prefixo
            </Label>
            <input
              id="footer-cl-prefix"
              value={form.changelog.prefix}
              onChange={(e) =>
                patchForm({ changelog: { ...form.changelog, prefix: e.target.value } })
              }
              placeholder="xatAjuda.com"
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="footer-cl-version" className="text-blue-100/90">
              Versão (opcional)
            </Label>
            <input
              id="footer-cl-version"
              value={form.changelog.version}
              onChange={(e) =>
                patchForm({ changelog: { ...form.changelog, version: e.target.value } })
              }
              placeholder={XAT_AJUDA_VERSION}
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="footer-cl-href" className="text-blue-100/90">
              Link
            </Label>
            <input
              id="footer-cl-href"
              value={form.changelog.href}
              onChange={(e) =>
                patchForm({ changelog: { ...form.changelog, href: e.target.value } })
              }
              placeholder="/changelog?produto=ajuda"
              className={painelInputClass}
            />
          </div>
        </div>
      </div>

      <div
        className={cn(
          painelSurfaceClass,
          'flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between'
        )}
      >
        <p className="font-inter text-xs text-slate-400">
          {dirty ? 'Há alterações por guardar.' : 'Tudo sincronizado com o servidor.'}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="ghost"
            className={cn(painelButtonOutlineClass, painelButtonResetRingClass)}
            onClick={handleReset}
            disabled={saving}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Repor padrão
          </Button>
          <Button
            type="button"
            className={painelButtonPrimaryClass}
            onClick={() => void handleSave()}
            disabled={saving || !dirty}
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'A guardar…' : 'Guardar alterações'}
          </Button>
        </div>
      </div>
    </div>
  );
}
